import React from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Calendar, Clock, Loader2, Check } from 'lucide-react';
import { formatDate } from './scheduleHelpers';
import { fadeInRight, fadeInUp, transitionMedium } from '../../utils/animations';

/**
 * Time Slot Selection Component
 */
const TimeSelect = ({
  t,
  language,
  selectedDate,
  selectedTeacher,
  selectedSlot,
  availableSlots,
  slotsLoading,
  booking,
  onSlotSelect,
  onBook,
  onBack
}) => {
  return (
    <motion.div
      initial={fadeInRight.initial}
      animate={fadeInRight.animate}
      transition={transitionMedium}
      className="space-y-6"
    >
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <button onClick={onBack} className="text-slate-500 hover:text-slate-900">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold text-slate-900">{t.selectTime}</h2>
          <div className="w-5" />
        </div>

        <p className="text-sm text-slate-500 mb-4">
          <Calendar className="w-4 h-4 inline mr-1" />
          {selectedDate && formatDate(selectedDate, language)}
        </p>

        {slotsLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : availableSlots.length === 0 ? (
          <div className="text-slate-500 text-center py-8">{t.noSlots}</div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {availableSlots.map((slot) => (
              <button
                key={slot.datetime}
                onClick={() => onSlotSelect(slot)}
                className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors
                  ${selectedSlot?.datetime === slot.datetime
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}
                `}
              >
                <Clock className="w-4 h-4 inline mr-1" />
                {slot.start_time}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation */}
      {selectedSlot && (
        <motion.div
          initial={fadeInUp.initial}
          animate={fadeInUp.animate}
          transition={transitionMedium}
          className="bg-white rounded-2xl shadow-sm p-6"
        >
          <h3 className="font-semibold text-slate-900 mb-4">{t.classDetails}</h3>
          <div className="space-y-3 mb-6">
            <div className="flex justify-between">
              <span className="text-slate-500">{t.teacher}</span>
              <span className="font-medium">{selectedTeacher?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">{t.date}</span>
              <span className="font-medium">{selectedDate && formatDate(selectedDate, language)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">{t.time}</span>
              <span className="font-medium">{selectedSlot.start_time} - {selectedSlot.end_time}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">{t.duration}</span>
              <span className="font-medium">50 {t.minutes}</span>
            </div>
          </div>

          <button
            onClick={onBook}
            disabled={booking}
            className="w-full bg-slate-900 text-white py-3 rounded-xl font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {booking ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t.booking}
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                {t.confirmBooking}
              </>
            )}
          </button>
        </motion.div>
      )}
    </motion.div>
  );
};

export default TimeSelect;
